import axios from 'axios';
import { API_BASE_URL, buildApiUrl } from '../../config/env';
import {
  CompanyCashoutRequest,
  CompanyClaimInvitationPreview,
  CompanyDriverDetail,
  CompanyDriverListResponse,
  CompanyLinkReviewItem,
  CompanyOwnerAuthResponse,
  CompanyOwnerCompany,
  CompanyOwnerDashboardSummary,
  CompanyPayoutDetails,
  CompanyOwnerSignUpRequest,
  CompanyOwnerUser,
} from '../../types/companyOwner';
import { sanitizeApiErrorMessage } from './errorMessages';
import { clearCompanyOwnerAuthFlow } from '../storage/companyOwnerFlow';
import { getWebSessionContext } from '../storage/authTokens';
import { attachRoleAuthInterceptors } from './authInterceptors';
import { logoutRoleSession, normalizeTokenBundle, refreshRoleSession } from './tokenRefresh';

const api = axios.create({
  baseURL: API_BASE_URL,
});

const publicApi = axios.create({
  baseURL: API_BASE_URL,
});

const openEndpoints = [
  '/auth-service/v1/auth/create',
  '/auth-service/v1/auth/login',
  '/auth-service/v1/auth/company-owner/login/verify-otp',
  '/auth-service/v1/auth/company-owner/login/resend-otp',
  '/auth-service/v1/auth/refresh',
  '/auth-service/v1/auth/logout',
  '/auth-service/v1/auth/verify-email',
  '/auth-service/v1/auth/resend-email-otp',
  '/auth-service/v1/auth/oauth2/authorize',
];

function endpointNeedsAuth(requestUrl: string): boolean {
  return !openEndpoints.some((endpoint) => requestUrl.startsWith(endpoint));
}

attachRoleAuthInterceptors(api, {
  role: 'companyOwner',
  endpointNeedsAuth,
  clearSession: clearCompanyOwnerAuthFlow,
});

function normalizeApiErrorMessage(rawMessage: string, fallbackMessage: string): string {
  const message = rawMessage.trim();
  if (!message) return fallbackMessage;

  if (/email is already in use!?/i.test(message)) {
    return 'This email is already registered. Try logging in instead.';
  }

  if (/company owner role required/i.test(message)) {
    return 'This account is not registered as a company owner.';
  }

  if (/email does not match/i.test(message) || /must match the invited company email/i.test(message)) {
    return 'Sign in with the invited company email address to use this invitation.';
  }

  if (/expired/i.test(message) && /claim|invitation|token/i.test(message)) {
    return 'This invitation link has expired. Ask your driver to send a new one.';
  }

  if (/company.*already.*claimed|already.*claimed.*company owner/i.test(message)) {
    return 'This company is already linked to another company owner account. Ask ShiftWagon support to review the company claim.';
  }

  if (/already used|already claimed/i.test(message)) {
    return 'This invitation link has already been used.';
  }

  if (/invalid/i.test(message) && /claim|invitation|token/i.test(message)) {
    return 'This invitation link is invalid. Check the email link and try again.';
  }

  if (/network error/i.test(message)) {
    return 'We could not reach the ShiftWagon server right now. Please try again.';
  }

  return sanitizeApiErrorMessage(message, fallbackMessage);
}

function extractErrorMessage(error: unknown, fallbackMessage: string): string {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;

    if (typeof responseData === 'string' && responseData.trim()) {
      return normalizeApiErrorMessage(responseData, fallbackMessage);
    }

    if (responseData && typeof responseData === 'object') {
      const data = responseData as Record<string, unknown>;
      const candidate =
        (typeof data.message === 'string' && data.message) ||
        (typeof data.error === 'string' && data.error) ||
        (typeof data.details === 'string' && data.details) ||
        (typeof data.detail === 'string' && data.detail) ||
        (typeof data.title === 'string' && data.title);

      if (candidate) {
        return normalizeApiErrorMessage(candidate, fallbackMessage);
      }
    }

    if (error.response?.status === 403) {
      return 'You do not have access to this company owner area.';
    }

    if (error.code === 'ERR_NETWORK' || /network error/i.test(error.message || '')) {
      return 'We could not reach the ShiftWagon server right now. Please try again.';
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return normalizeApiErrorMessage(error.message, fallbackMessage);
  }

  return fallbackMessage;
}

function mapCompanyOwnerUser(user: any): CompanyOwnerUser {
  return {
    id: String(user.id),
    name: user.name,
    username: user.username,
    email: user.email,
    roles: user.roles ?? [],
    emailVerified: user.emailVerified,
    phone: user.phone,
    phoneVerified: user.phoneVerified,
    googleLinked: user.googleLinked,
    onboardingsStep: user.onboardingsStep,
  };
}

function mapAuthResponse(response: any): CompanyOwnerAuthResponse {
  const tokenBundle = normalizeTokenBundle(response);
  return {
    success: typeof response.success === 'boolean' ? response.success : Boolean(tokenBundle.accessToken),
    message: response.message ?? '',
    token: tokenBundle.accessToken ?? undefined,
    accessToken: tokenBundle.accessToken,
    refreshToken: tokenBundle.refreshToken,
    accessTokenExpiresAt: tokenBundle.accessTokenExpiresAt,
    refreshTokenExpiresAt: tokenBundle.refreshTokenExpiresAt,
    userResponse: response.userResponse ? mapCompanyOwnerUser(response.userResponse) : undefined,
    emailOtpRequired: Boolean(response.emailOtpRequired ?? response.otpRequired),
  };
}

function mapCompany(response: any): CompanyOwnerCompany {
  return {
    companyId: String(response.companyId),
    ownerUserId: String(response.ownerUserId),
    legalName: response.legalName,
    tradingName: response.tradingName,
    registrationNumber: response.registrationNumber,
    primaryEmail: response.primaryEmail,
    primaryPhone: response.primaryPhone,
    status: response.status,
    linkedDrivers: Number(response.linkedDrivers ?? 0),
    pendingReviewLinks: Number(response.pendingReviewLinks ?? 0),
    manualReviewLinks: Number(response.manualReviewLinks ?? 0),
  };
}

function mapClaimInvitationPreview(response: any): CompanyClaimInvitationPreview {
  return {
    companyName: response.companyName ?? '',
    maskedCompanyEmail: response.maskedCompanyEmail ?? '',
    expiresAt: response.expiresAt ?? null,
    invitedBy: response.invitedBy ?? null,
  };
}

function mapDashboardSummary(response: any): CompanyOwnerDashboardSummary {
  return {
    totalLinkedDrivers: Number(response.totalLinkedDrivers ?? 0),
    activeDriversNow: Number(response.activeDriversNow ?? 0),
    onlineDrivers: Number(response.onlineDrivers ?? 0),
    driversWithTripsToday: Number(response.driversWithTripsToday ?? 0),
    tripsToday: Number(response.tripsToday ?? 0),
    earningsToday: response.earningsToday,
    earningsThisWeek: response.earningsThisWeek,
    earningsThisMonth: response.earningsThisMonth,
    payoutSummary: response.payoutSummary
      ? {
          payoutDetailsPresent: Boolean(response.payoutSummary.payoutDetailsPresent),
          payoutStatus: response.payoutSummary.payoutStatus ?? null,
          requestedCashoutTotal: response.payoutSummary.requestedCashoutTotal ?? 0,
          approvedCashoutTotal: response.payoutSummary.approvedCashoutTotal ?? 0,
          estimatedAvailableBalance: response.payoutSummary.estimatedAvailableBalance ?? null,
          earnedThisMonth: response.payoutSummary.earnedThisMonth ?? null,
          splitAppliedThisMonth: response.payoutSummary.splitAppliedThisMonth ?? null,
          estimatedPendingSettlement: response.payoutSummary.estimatedPendingSettlement ?? null,
          confirmedSettledThisMonth: response.payoutSummary.confirmedSettledThisMonth ?? null,
          onHoldAmount: response.payoutSummary.onHoldAmount ?? null,
          exceptionAmount: response.payoutSummary.exceptionAmount ?? null,
          settlementReconciliationAvailable: Boolean(
            response.payoutSummary.settlementReconciliationAvailable,
          ),
          lastSettlementReconciledAt: response.payoutSummary.lastSettlementReconciledAt ?? null,
          note: response.payoutSummary.note ?? null,
        }
      : null,
  };
}

function mapPayoutDetails(response: any): CompanyPayoutDetails {
  return {
    companyId: String(response.companyId ?? ''),
    payoutDetailsPresent: Boolean(response.payoutDetailsPresent),
    payoutStatus: response.payoutStatus ?? 'MISSING',
    bankName: response.bankName ?? null,
    maskedAccountNumber: response.maskedAccountNumber ?? null,
    accountHolderName: response.accountHolderName ?? null,
    branchCode: response.branchCode ?? null,
    accountType: response.accountType ?? null,
    bankLetterPresent: Boolean(response.bankLetterPresent),
    bankLetterFileName: response.bankLetterFileName ?? null,
    bankLetterUploadedAt: response.bankLetterUploadedAt ?? null,
    updatedAt: response.updatedAt ?? null,
  };
}

function mapDriverListResponse(response: any): CompanyDriverListResponse {
  return {
    items: Array.isArray(response.items)
      ? response.items.map((item: any) => ({
          driverId: String(item.driverId),
          name: item.name,
          email: item.email,
          accountType: item.accountType,
          online: Boolean(item.online),
          onTrip: Boolean(item.onTrip),
          lastSeenAt: item.lastSeenAt,
          rawCompanyName: item.rawCompanyName,
          linkStatus: item.linkStatus,
          matchingConfidence: item.matchingConfidence,
          dailyEarnings: item.dailyEarnings,
          weeklyEarnings: item.weeklyEarnings,
          monthlyEarnings: item.monthlyEarnings,
          completedTrips: item.completedTrips,
        }))
      : [],
    page: Number(response.page ?? 0),
    size: Number(response.size ?? 20),
    total: Number(response.total ?? 0),
  };
}

function mapDriverDetail(response: any): CompanyDriverDetail {
  return {
    driverId: String(response.driverId),
    name: response.name,
    email: response.email,
    phoneE164: response.phoneE164,
    online: Boolean(response.online),
    onTrip: Boolean(response.onTrip),
    lastSeenAt: response.lastSeenAt,
    lastKnownLat: response.lastKnownLat,
    lastKnownLng: response.lastKnownLng,
    rawCompanyName: response.rawCompanyName,
    rawCompanyEmail: response.rawCompanyEmail,
    rawCompanyPhone: response.rawCompanyPhone,
    rawCompanyRegNumber: response.rawCompanyRegNumber,
    linkStatus: response.linkStatus,
    matchingConfidence: response.matchingConfidence,
    matchingNotes: response.matchingNotes,
    completedTrips: Number(response.completedTrips ?? 0),
    tripsToday: Number(response.tripsToday ?? 0),
    dailyEarnings: Number(response.dailyEarnings ?? 0),
    weeklyEarnings: Number(response.weeklyEarnings ?? 0),
    monthlyEarnings: Number(response.monthlyEarnings ?? 0),
    totalEarnings: Number(response.totalEarnings ?? 0),
    acceptanceRate30d: Number(response.acceptanceRate30d ?? 0),
    avgResponseSec30d: Number(response.avgResponseSec30d ?? 0),
    ratings: Number(response.ratings ?? 0),
    ratingCount: Number(response.ratingCount ?? 0),
  };
}

function mapReviewItem(response: any): CompanyLinkReviewItem {
  return {
    linkId: String(response.linkId),
    driverId: String(response.driverId),
    driverName: response.driverName,
    driverEmail: response.driverEmail,
    rawCompanyName: response.rawCompanyName,
    rawCompanyEmail: response.rawCompanyEmail,
    rawCompanyPhone: response.rawCompanyPhone,
    rawCompanyRegNumber: response.rawCompanyRegNumber,
    linkStatus: response.linkStatus,
    linkageSource: response.linkageSource,
    matchingConfidence: response.matchingConfidence,
    matchingNotes: response.matchingNotes,
  };
}

function mapCashout(response: any): CompanyCashoutRequest {
  return {
    requestId: String(response.requestId),
    amount: response.amount,
    currency: response.currency,
    status: response.status,
    note: response.note,
    reviewNotes: response.reviewNotes,
    created: response.created,
    updated: response.updated,
  };
}

export function getCompanyOwnerGoogleSignUpUrl(): string {
  return buildApiUrl('auth-service/v1/auth/oauth2/authorize/google?role=company_owner&client=web');
}

export async function companyOwnerSignUp(
  payload: CompanyOwnerSignUpRequest,
): Promise<CompanyOwnerAuthResponse> {
  try {
    const response = await publicApi.post('/auth-service/v1/auth/create', payload);
    return mapAuthResponse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not create company owner account.'));
  }
}

export async function companyOwnerSignIn(payload: {
  email: string;
  password: string;
  role?: string;
}): Promise<CompanyOwnerAuthResponse> {
  try {
    const response = await publicApi.post('/auth-service/v1/auth/login', {
      ...payload,
      ...getWebSessionContext(),
    });
    return mapAuthResponse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not sign in.'));
  }
}

export async function requestCompanyOwnerPasswordReset(email: string): Promise<{
  challengeId: string;
  message: string;
}> {
  try {
    const response = await publicApi.post('/auth-service/v1/auth/forgot-password', { email });
    return {
      challengeId: String(response.data?.challengeId ?? ''),
      message: response.data?.message ?? 'If the account exists, a password reset code has been sent.',
    };
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to send password reset code. Please try again.'));
  }
}

export async function resetCompanyOwnerPassword(payload: {
  challengeId: string;
  code: string;
  newPassword: string;
}): Promise<void> {
  try {
    await publicApi.post('/auth-service/v1/auth/reset-password', payload);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to reset password. Please check your code and try again.'));
  }
}

export async function verifyCompanyOwnerLoginOtp(payload: {
  email: string;
  password: string;
  code: string;
}): Promise<CompanyOwnerAuthResponse> {
  try {
    const response = await publicApi.post('/auth-service/v1/auth/company-owner/login/verify-otp', {
      ...payload,
      ...getWebSessionContext(),
    });
    return mapAuthResponse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not verify the login code.'));
  }
}

export async function resendCompanyOwnerLoginOtp(payload: {
  email: string;
  password: string;
  role?: string;
}): Promise<CompanyOwnerAuthResponse> {
  try {
    const response = await publicApi.post('/auth-service/v1/auth/company-owner/login/resend-otp', payload);
    return mapAuthResponse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not resend the login code.'));
  }
}

export async function verifyCompanyOwnerEmail(
  email: string,
  code: string,
): Promise<CompanyOwnerAuthResponse> {
  try {
    const response = await publicApi.post('/auth-service/v1/auth/verify-email', {
      email,
      code,
      ...getWebSessionContext(),
    });
    return mapAuthResponse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Email verification failed.'));
  }
}

export async function refreshCompanyOwnerAuthSession(): Promise<string | null> {
  return refreshRoleSession('companyOwner');
}

export async function logoutCompanyOwnerAuthSession(): Promise<void> {
  await logoutRoleSession('companyOwner');
  clearCompanyOwnerAuthFlow();
}

export async function resendCompanyOwnerEmailOtp(email: string): Promise<void> {
  try {
    await publicApi.post('/auth-service/v1/auth/resend-email-otp', { email });
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not resend the email code.'));
  }
}

export async function fetchCompanyOwnerById(
  userId: string,
  token: string,
): Promise<CompanyOwnerUser> {
  try {
    const response = await api.get(`/user-management/api/users/${encodeURIComponent(userId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return mapCompanyOwnerUser(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load company owner details.'));
  }
}

export async function fetchMyCompany(): Promise<CompanyOwnerCompany | null> {
  try {
    const response = await api.get('/driver-management/api/company-owner/company');
    return mapCompany(response.data);
  } catch (error) {
    if (axios.isAxiosError(error) && (error.response?.status ?? 0) === 404) {
      return null;
    }
    throw new Error(extractErrorMessage(error, 'Could not load company details.'));
  }
}

export async function previewCompanyClaimInvitation(
  token: string,
): Promise<CompanyClaimInvitationPreview> {
  try {
    const response = await publicApi.get('/driver-management/api/company-owner/claim-invitations/preview', {
      params: { token },
    });
    return mapClaimInvitationPreview(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load the invitation preview.'));
  }
}

export async function acceptCompanyClaimInvitation(token: string): Promise<CompanyOwnerCompany> {
  try {
    const response = await api.post('/driver-management/api/company-owner/claim-invitations/accept', {
      token,
    });
    return mapCompany(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not accept the company invitation.'));
  }
}

export async function fetchCompanyDashboardSummary(): Promise<CompanyOwnerDashboardSummary> {
  try {
    const response = await api.get('/driver-management/api/company-owner/dashboard/summary');
    return mapDashboardSummary(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load the company dashboard.'));
  }
}

export async function fetchCompanyPayoutDetails(): Promise<CompanyPayoutDetails> {
  try {
    const response = await api.get('/driver-management/api/company-owner/payout-details');
    return mapPayoutDetails(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load company payout details.'));
  }
}

export async function upsertCompanyPayoutDetails(payload: {
  accountHolderName: string;
  bankName: string;
  accountNumber: string;
  branchCode?: string;
  accountType: string;
  bankLetter?: File | null;
}): Promise<CompanyPayoutDetails> {
  try {
    const formData = new FormData();
    formData.append('accountHolderName', payload.accountHolderName);
    formData.append('bankName', payload.bankName);
    formData.append('accountNumber', payload.accountNumber);
    if (payload.branchCode?.trim()) {
      formData.append('branchCode', payload.branchCode.trim());
    }
    formData.append('accountType', payload.accountType);
    if (payload.bankLetter) {
      formData.append('bankLetter', payload.bankLetter);
    }

    const response = await api.put('/driver-management/api/company-owner/payout-details', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return mapPayoutDetails(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not save company payout details.'));
  }
}

export async function fetchCompanyDrivers(params: {
  query?: string;
  online?: boolean;
  onTrip?: boolean;
  linkStatus?: string;
  page?: number;
  size?: number;
}): Promise<CompanyDriverListResponse> {
  try {
    const response = await api.get('/driver-management/api/company-owner/drivers', {
      params,
    });
    return mapDriverListResponse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load company drivers.'));
  }
}

export async function fetchCompanyDriverDetail(driverId: string): Promise<CompanyDriverDetail> {
  try {
    const response = await api.get(`/driver-management/api/company-owner/drivers/${encodeURIComponent(driverId)}`);
    return mapDriverDetail(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load driver details.'));
  }
}

export async function fetchCompanyReviewMatches(
  status?: string,
): Promise<CompanyLinkReviewItem[]> {
  try {
    const response = await api.get('/driver-management/api/company-owner/matches', {
      params: status ? { status } : undefined,
    });
    return Array.isArray(response.data) ? response.data.map(mapReviewItem) : [];
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load company link matches.'));
  }
}

export async function reviewCompanyMatch(payload: {
  linkId: string;
  decision: 'CONFIRM' | 'REJECT';
  notes?: string;
}): Promise<CompanyLinkReviewItem> {
  try {
    const response = await api.post(
      `/driver-management/api/company-owner/matches/${encodeURIComponent(payload.linkId)}/review`,
      {
        decision: payload.decision,
        ...(payload.notes ? { notes: payload.notes } : {}),
      },
    );
    return mapReviewItem(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not review the company-driver link.'));
  }
}

export async function createCompanyCashoutRequest(payload: {
  amount: number;
  note?: string;
}): Promise<CompanyCashoutRequest> {
  try {
    const response = await api.post('/driver-management/api/company-owner/cashout-requests', payload);
    return mapCashout(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not create the cashout request.'));
  }
}

export async function fetchCompanyCashoutRequests(): Promise<CompanyCashoutRequest[]> {
  try {
    const response = await api.get('/driver-management/api/company-owner/cashout-requests');
    return Array.isArray(response.data) ? response.data.map(mapCashout) : [];
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load cashout requests.'));
  }
}
